/**
 * Sessions this browser has been in, remembered across tabs and restarts.
 *
 * The active session lives in sessionStorage, which is emptied when the tab
 * closes. That was survivable while a session ID was something you chose and
 * shared. It is not survivable now that regenerating forks: the new ID is minted
 * by the server, announced in a toast, and never typed by anyone — so closing
 * the tab could leave a roster with no route back to it.
 *
 * localStorage, deliberately, and it holds nothing but IDs and names the user
 * already typed. It is a convenience list, not an auth record: joining still
 * goes through the server, which is what decides whether a session exists.
 */

const STORAGE_KEY = 'ac_roster_recent_sessions';

/** Enough to cover a shift's worth of regenerations without becoming a wall. */
const MAX_RECENT = 8;

export interface RecentSession {
  sessionId: string;
  userName: string;
  /** ISO timestamp of the last time this browser entered the session. */
  lastUsed: string;
  /** Set when the session was minted by regenerating out of another one. */
  forkedFrom?: string;
}

function isRecentSession(value: unknown): value is RecentSession {
  const v = value as RecentSession | null;
  return !!v && typeof v.sessionId === 'string' && typeof v.userName === 'string'
    && typeof v.lastUsed === 'string';
}

export function listRecentSessions(): RecentSession[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isRecentSession) : [];
  } catch {
    return [];
  }
}

function write(sessions: RecentSession[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions.slice(0, MAX_RECENT)));
  } catch {
    // A full or disabled localStorage costs the user a convenience, never a session.
  }
}

/**
 * Record entering a session, moving it to the front of the list.
 *
 * `forkedFrom` is preserved across re-entries: a session only learns where it
 * came from once, at the moment it is minted.
 */
export function recordSession(
  sessionId: string,
  userName: string,
  forkedFrom?: string,
): void {
  const existing = listRecentSessions();
  const previous = existing.find((s) => s.sessionId === sessionId);
  write([
    {
      sessionId,
      userName,
      lastUsed: new Date().toISOString(),
      forkedFrom: forkedFrom ?? previous?.forkedFrom,
    },
    ...existing.filter((s) => s.sessionId !== sessionId),
  ]);
}

/** Drop one entry — for a session the server no longer recognises. */
export function forgetSession(sessionId: string): void {
  write(listRecentSessions().filter((s) => s.sessionId !== sessionId));
}
